import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, IconButton, InputAdornment, SvgIcon, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Plus, SearchSm } from '../../../assets';
import BackButton from '../../../components/shared/actions/BackButton';
import useLogError from '../../../hooks/useLogError';
import { SuccessResponse_1OfPagedData_1OfUser, User } from '../../../app/services/api/generated';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { UserContext } from '../../../components/shared/useUser';
import { ListPageTemplate } from '../../../components/page-templates/list-page/ListPageTemplate';
import { QuickFilterBar, QuickFilterGroup } from '../../../components/shared/filters/QuickFilterBar';
import { TableSortDirection } from '../../../components/shared/tables/TableSection';
import { useUsersTable } from './components/UsersTableSection';

const ROWS_PER_PAGE = 10;

// Mirrors Orderly.Inventory.Common.Enums.UserStatusFilter (Active = 0, Invited = 1).
const STATUS_FILTER_VALUES: Record<string, number> = { active: 0, invited: 1 };
// Mirrors Orderly.Inventory.Common.Enums.UserLevel (StoreUser = 0, FranchiseUser = 1, Admin = 2).
// lazy: backend has no dedicated Tenant value yet - Tenant users are folded into Admin(2) here as a placeholder, needs a backend enum update.
const LEVEL_FILTER_VALUES: Record<string, number> = { store: 0, franchise: 1, tenant: 2 };

const UserManagementUsersPage = () => {
  const { searchUsers } = useNswagClient();
  const { t } = useTranslation('manageUsers');
  const { logError } = useLogError();
  const { hasPermissionTo } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState<User[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') ?? '');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(() => searchParams.get('search') ?? '');
  // status maps to the new `status` search param, derived server-side from Clerk sign-in history
  const [statusFilter, setStatusFilter] = useState<readonly string[]>([]);
  // level maps to the new `level` search param; backend filtering not yet implemented (see UserRetrievalService TODO)
  const [levelFilter, setLevelFilter] = useState<readonly string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('emailAddress');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('asc');

  const isInitialMount = useRef(true);

  const loadData = useCallback((
    currentPage: number,
    currentSortBy: string,
    currentSortDirection: TableSortDirection,
    term: string,
    level: string,
    status: string,
  ) => {
    setIsLoading(true);

    const skip = currentPage * ROWS_PER_PAGE;
    searchUsers(
      skip,
      ROWS_PER_PAGE,
      undefined,
      undefined,
      undefined,
      currentSortBy,
      currentSortDirection,
      term || undefined,
      LEVEL_FILTER_VALUES[level],
      STATUS_FILTER_VALUES[status],
    )
      .then((result: SuccessResponse_1OfPagedData_1OfUser) => {
        if (result?.data) {
          setUsers(result.data.data ?? []);
          setTotalCount(result.data.totalCount ?? 0);
        }
      })
      .catch((error) => logError(error))
      .finally(() => setIsLoading(false));
  }, [searchUsers, logError]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      loadData(0, sortBy, sortDirection, appliedSearchTerm, levelFilter[0] ?? '', statusFilter[0] ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadData(newPage, sortBy, sortDirection, appliedSearchTerm, levelFilter[0] ?? '', statusFilter[0] ?? '');
  };

  const handleSortChange = (newSortBy: string, newSortDirection: TableSortDirection) => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
    setPage(0);
    loadData(0, newSortBy, newSortDirection, appliedSearchTerm, levelFilter[0] ?? '', statusFilter[0] ?? '');
  };

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
    setPage(0);
    setSearchParams(searchTerm ? { search: searchTerm } : {});
    loadData(0, sortBy, sortDirection, searchTerm, levelFilter[0] ?? '', statusFilter[0] ?? '');
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setAppliedSearchTerm('');
    setPage(0);
    setSearchParams({});
    loadData(0, sortBy, sortDirection, '', levelFilter[0] ?? '', statusFilter[0] ?? '');
  };

  const handleStatusFilterChange = (value: readonly string[]) => {
    setStatusFilter(value);
    setPage(0);
    loadData(0, sortBy, sortDirection, appliedSearchTerm, levelFilter[0] ?? '', value[0] ?? '');
  };

  const handleLevelFilterChange = (value: readonly string[]) => {
    setLevelFilter(value);
    setPage(0);
    loadData(0, sortBy, sortDirection, appliedSearchTerm, value[0] ?? '', statusFilter[0] ?? '');
  };

  const refreshCurrentPage = () => {
    loadData(page, sortBy, sortDirection, appliedSearchTerm, levelFilter[0] ?? '', statusFilter[0] ?? '');
  };

  const maxPages = Math.max(1, Math.ceil(totalCount / ROWS_PER_PAGE));

  const quickFilterGroups: QuickFilterGroup[] = [
    {
      id: 'status',
      multiple: false,
      value: statusFilter,
      onChange: handleStatusFilterChange,
      options: [
        { id: 'invited', label: t('invited') },
        { id: 'active', label: t('active') },
      ],
    },
    {
      id: 'level',
      multiple: false,
      value: levelFilter,
      onChange: handleLevelFilterChange,
      options: [
        { id: 'tenant', label: t('tenantLevel') },
        { id: 'franchise', label: t('franchiseLevel') },
        { id: 'store', label: t('storeLevel') },
      ],
    },
  ];

  const { table, dialogs } = useUsersTable({
    list: users,
    totalCount,
    loadData: refreshCurrentPage,
    isLoading,
    page,
    maxPages,
    onPageChange: handlePageChange,
    sortBy,
    sortDirection,
    onSortChange: handleSortChange,
  });

  return (
    <>
      <ListPageTemplate
        title={t('users')}
        subtitle={t('manageUsers')}
        actions={(
          <Button
            variant="primary"
            size="lg"
            disabled={!hasPermissionTo(['UserManagementCreate'])}
            onClick={() => navigate('/user-management/users/create')}
            startIcon={<SvgIcon><Plus fill="white" /></SvgIcon>}
          >
            {t('newUser')}
          </Button>
        )}
        quickFilterContent={(
          <QuickFilterBar
            groups={quickFilterGroups}
            searchContent={(
              <TextField
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
                size='small'
                placeholder={t('search')}
                sx={{ width: 280, mb: 0 }}
                InputProps={{
                  startAdornment: <InputAdornment position='start'><SvgIcon fontSize='small'><SearchSm /></SvgIcon></InputAdornment>,
                  endAdornment: searchTerm && (
                    <InputAdornment position='end'>
                      <IconButton size='small'
                        aria-label={t('clearFilters')}
                        onClick={handleClearSearch}>
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        )}
        loading={isLoading && users.length === 0}
        loadingLabel={t('users')}
        table={table}
      >
        {dialogs}
      </ListPageTemplate>
    </>
  );
};

export default UserManagementUsersPage;
